/**
 * Render a UTC ISO instant as a compact city-local datetime, e.g. "Tue 28 Jul, 13:00".
 * Uses the native Intl API (no extra dependency); falls back to UTC when unknown.
 */
export function formatCityTime(iso, timezone) {
    const date = typeof iso === "string" ? new Date(iso) : iso;
    return new Intl.DateTimeFormat("en-GB", {
        timeZone: timezone || "UTC",
        weekday: "short",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    }).format(date);
}
/**
 * An event's local date for the review deck: always with the year, and never
 * with a clock time.
 *
 * The extractor stores local midnight in `startAt` when a source names no time,
 * so printing that instant's clock put a fabricated `00:00` next to a `Time`
 * claim reading "not stated". The time is its own claim on the card, so showing
 * it here as well would print the same value twice. The year is always shown
 * because this queue spans years, and a bare "01 Jan" cannot be checked against
 * a poster.
 */
export function formatEventDate(iso, timezone) {
    const date = typeof iso === "string" ? new Date(iso) : iso;
    if (Number.isNaN(date.getTime()))
        return "not stated";
    const zone = timezone || "UTC";
    const weekday = new Intl.DateTimeFormat("en-GB", { timeZone: zone, weekday: "short" }).format(date);
    const day = new Intl.DateTimeFormat("en-GB", {
        timeZone: zone,
        day: "numeric",
        month: "short",
        year: "numeric",
    }).format(date);
    return `${weekday} ${day}`;
}
/** Anton lacks Turkish glyphs — same workaround as the post template. */
export function toAsciiUpper(text) {
    return text
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase();
}
/**
 * Local `HH:MM:SS` in an IANA timezone. `at` pins the instant: a document that
 * prints this clock is server-rendered and then hydrated, and the browser's
 * first render has to print exactly the reading the document shipped, so the
 * renderer passes the instant it rendered from. Without `at`, the reading is the
 * current time.
 */
export function clockNow(tz, at) {
    return new Intl.DateTimeFormat("en-GB", {
        timeZone: tz,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    }).format(at ?? new Date());
}
//# sourceMappingURL=time.js.map